import Alert from 'core-system/Icons/Misc/Alert'
import Modal, { ModalBody, ModalHeader } from 'core-system/Modal'
import pxToRem from 'core-system/utils/pxToRem'
import React, { useCallback, useEffect, useState } from 'react'
import { StaticAssetsUrl } from 'redux/config/services/Constants'
import styled from 'styled-components'
import RandomUtils from 'shared/RandomUtils'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import { SuccessEvent, useFinchConnect } from '@tryfinch/react-connect'
import Request from 'redux/config/services/Request'
import SegmentService from 'redux/config/services/SegmentService'

const Container = styled.div`
  padding: 1.5rem;
  border: 0.0625rem solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: ${(props) => props.theme.pxToRem(15)};
  ${(props) => props.theme.typography.body1};
  color: ${(props) => props.theme.palette.text.primary};
`

const ImgContainer = styled.div<{ imgUrl?: string }>`
  background-image: url(${(props) => props.imgUrl});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  border-radius: 0.5rem;
  width: ${(props) => props.theme.pxToRem(142)};
  height: ${(props) => props.theme.pxToRem(142)};
  margin-bottom: 1.5rem;
`

const Heading = styled.div`
  ${(props) => props.theme.typography.action2};
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom: 0.5rem;
  text-transform: capitalize;
`

const SubText = styled.div`
  ${(props) => props.theme.typography.body2};
  color: ${(props) => props.theme.palette.text.placeholder};
`

interface HrisReconnectModalProps {
  providerName: string
  openModal: boolean
  closeModal: (e: React.MouseEvent<any>) => void
  onSuccess: (e: SuccessEvent) => void
}

const HrisReconnectModal = (props: HrisReconnectModalProps) => {
  const { providerName, openModal, closeModal } = props

  const [sessionId, setSessionId] = useState<string | null>(null)
  const [shouldOpen, setShouldOpen] = useState(false)

  const onSuccess = (e: SuccessEvent) => {
    props.onSuccess(e)
  }

  const onError = ({ errorMessage }) => {
    SegmentService.track('hris-reauth-error', {
      msg: errorMessage,
    })
  }
  const onClose = () => SegmentService.track('hris-reauth-modal-closed')

  const { open } = useFinchConnect({
    sessionId: sessionId || undefined,
    onSuccess: onSuccess,
    onError: onError,
    onClose: onClose,
  })

  const handleClose = useCallback(
    (e: React.MouseEvent<any>) => {
      closeModal(e)
    },
    [closeModal]
  )

  const handleReconnect = async (
    e: React.MouseEvent<HTMLButtonElement | any>
  ) => {
    try {
      const response = await Request.get('/finch/reauth/')
      setSessionId(response.data.sessionId)
      setShouldOpen(true)
      handleClose(e)
    } catch (err) {
      console.error('Failed to get Finch sessionId', err)
    }
  }

  useEffect(() => {
    if (shouldOpen && sessionId) {
      open()
      setShouldOpen(false)
    }
  }, [sessionId, shouldOpen, open])

  const formattedProviderName =
    RandomUtils.formatFinchProviderName(providerName)

  return (
    <Modal open={openModal} onClose={handleClose} width={pxToRem(514)}>
      <ModalHeader
        title={`Manage ${formattedProviderName} Integration`}
        style={{ textTransform: 'capitalize' }}
      />
      <ModalBody paddingBottom='3.25rem' paddingTop='1.5rem'>
        <Container>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <ImgContainer
              imgUrl={`${StaticAssetsUrl}/hris_provider_logos/reconnect.png`}
            />
          </div>
          <div>
            <Heading>Please Reconnect</Heading>
            <div>
              Something went wrong please reconnect your Fleet account with{' '}
              <span style={{ textTransform: 'capitalize' }}>
                {formattedProviderName}.
              </span>
            </div>
          </div>
          <PrimaryButton
            onClick={handleReconnect}
            style={{ marginBottom: '1rem', marginTop: '1.5rem' }}
            fullWidth
            startIcon={<Alert />}
          >
            Reconnect
          </PrimaryButton>
          <SubText>
            Your information is handled securely with end-to-end encryption.
          </SubText>
        </Container>
      </ModalBody>
    </Modal>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  HrisReconnectModal.displayName = 'HrisReconnectModal'
}

export default HrisReconnectModal
